import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DocsAnimatedBackground from '../DocsAnimatedBackground';
import '../DocumentationPageLanding.css';

const DocumentationPage: React.FC = () => {
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLUListElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  return (
    <div className="docs-landing-container">
      <DocsAnimatedBackground />
      <div className={`mobile-menu-backdrop ${mobileMenuOpen ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}></div>
      <header style={{ background: 'rgba(10,10,10,0.65)', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <nav className="container">
          <button className="logo" onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>VirtualFit</button>
          <ul ref={mobileMenuRef} className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <li><a href="/">Home</a></li>
            <li><a href="/docs" className="active">Documentation</a></li>
            <li><a href="/demo-store">Demo Store</a></li>
          </ul>
          <button className="cta-button" onClick={() => navigate('/login')}>Login</button>
          <button className="cta-button" onClick={() => navigate('/register')}>Sign Up</button>
          <button ref={hamburgerRef} className="mobile-menu-toggle" onClick={toggleMobileMenu}>
            <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}></span>
          </button>
        </nav>
      </header>
      <div className="docs-landing-content">
        <h1>Platform Documentation</h1>
        <p className="subtitle">Welcome! This guide will help you get started with the Virtual Try-On platform, understand the app's main features, and integrate the widget into your own project.</p>

        <h2 id="navigation">Navigation & Pages</h2>
        <ul>
          <li><b>Dashboard:</b> Overview of your account, usage, and quick links.</li>
          <li><b>Analytics:</b> View usage statistics and try-on analytics.</li>
          <li><b>Products:</b> Manage your product catalog and images.</li>
          <li><b>Widget:</b> Customize try-on widget appearance and behavior.</li>
          <li><b>Integration:</b> Get API key and widget embed code.</li>
          <li><b>Subscription:</b> View billing, credits, and plan details.</li>
          <li><b>Demo Store:</b> Experience the widget as a customer.</li>
        </ul>

        <h2 id="integration">Seamless Integration in 3 Steps</h2>
        <ol>
          <li>
            <b>1. Add the Widget Container</b><br />
            <code>&lt;div id="try-on-widget"&gt;&lt;/div&gt;</code>
          </li>
          <li>
            <b>2. Include the Widget Script</b><br />
            <code>&lt;script src="https://virtualfit.puniminds.com/virtual-tryon-widget.min.js"&gt;&lt;/script&gt;</code>
          </li>
          <li>
            <b>3. Initialize the Widget</b><br />
            <pre>{`VirtualTryOnWidget.init({
  apiKey: 'YOUR_API_KEY',  // pragma: allowlist secret
  productId: 'PRODUCT_ID',
  // ...other config options
});`}</pre>
            <span className="note">Replace <b>YOUR_API_KEY</b> and <b>PRODUCT_ID</b> with your actual values from the Integration page.</span>
          </li>
        </ol>

        <h2 id="example">Widget Integration Example</h2>
        <pre className="code-block">{`<!-- Virtual Try-On Widget Embed -->
<div id="try-on-widget"></div>
<script src="https://virtualfit.puniminds.com/virtual-tryon-widget.min.js"></script>
<script>
  VirtualTryOnWidget.init({
    apiKey: 'YOUR_API_KEY',// pragma: allowlist secret
    productId: 'PRODUCT_ID',
    // ...other config
  });
</script>`}</pre>
        <span className="note">Place this code in your product page HTML (WordPress, Shopify, etc.)</span>

        <h2 id="usage">Using the Platform</h2>
        <ul className="usage-list">
          <li>Use the <b>Demo Store</b> to experience the widget as a customer</li>
          <li>Go to <b>Integration</b> to get your API key and embed code</li>
          <li>Manage products in the <b>Products</b> section</li>
          <li>Check <b>Analytics</b> for try-on stats and engagement</li>
          <li>Customize your widget in the <b>Widget</b> section</li>
          <li>Monitor your plan in <b>Subscription</b></li>
        </ul>

        <h2 id="faq">FAQ</h2>
        <ul className="faq-list">
          <li>
            <b>Can I use the widget on WordPress/Shopify?</b>
            <p>Yes! Paste the embed code into your product page HTML or theme.</p>
          </li>
          <li>
            <b>Where do I find my API key?</b>
            <p>In the <b>Integration</b> page after logging in.</p>
          </li>
          <li>
            <b>How do I get support?</b>
            <p>Use the chatbot or contact us via the dashboard.</p>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DocumentationPage;
